import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { DataTable, OptionAutocomplete, OptionSelect } from '../../../../ui';
import { colors } from '../../../../theme/tokens';
import actions from '../../../../redux/actions';
import { OperationStatus } from '../../../../types/OperationStatus';
import { DesktopType } from '../../../../types/DesktopType';
import { DevicePlatform } from '../../../../types/DevicePlatform';
import { getPlatformPrettyName } from '../../../../services/deviceService';

const mapStateToProps = (state) => {
  return {
    addBrowserStatus: state.addBrowserStatus,
    user: state.user,
    browserNames: state.browsers.names,
    browserVersions: state.browsers.versions,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateUser: (data) => dispatch(actions.updateUser(data)),
    saveUser: (data) => dispatch(actions.saveUser(data)),
    addBrowser: (data) => dispatch(actions.addBrowser(data)),
    deleteBrowser: (data) => dispatch(actions.deleteBrowser(data)),
  };
};

class Browsers extends Component<any> {
  public state: any;
  private platformTypeOptions: any[];  

  constructor(props) {
    super(props);
    this.state = {
      newBrowserId: '',
      newBrowserVersionId: '',
      browserVersions: [],
    };

    this.platformTypeOptions = Object.entries(DevicePlatform).reduce((result, [key, val]) => {
      if (val !== DevicePlatform.Desktop && val !== DevicePlatform.WindowsDesktop && val !== DevicePlatform.WindowsLaptop ) {
        result.push({
          text: getPlatformPrettyName(val),
          key: DevicePlatform[key],
          value: DevicePlatform[key],
        });
      }        
      return result;
    }, [] as any[]);
    
  }

  onChange = (event, data) => {
    const user = { ...this.props.user };

    switch (data.name) {
      // Stored right away (Add/Delete save immediately too, so the tab has no Save button)
      case 'desktopType': {
        user.desktopTypes = [data.value];
        this.props.updateUser(user);
        const toSave = { ...user };
        delete toSave.avatarUrl;
        this.props.saveUser(toSave);
        break;
      }

      case 'newBrowserId': {
        const browserVersions = this.props.browserVersions.filter(
          (el) => el.browserId === data.value
        );

        return this.setState({
          ...this.state,
          newBrowserId: data.value,
          newBrowserVersionId: '',
          browserVersions,
        });
      }

      case 'newBrowserVersionId': {
        return this.setState({
          ...this.state,
          newBrowserVersionId: data.value,
        });
      }

      default:
        return;
    }
  };

  onAddBrowser = () => {
    // TODO maybe remove this and call from render()?
    this.props.addBrowser({
      platformId: [...this.props.user.desktopTypes][0],
      browserId: this.state.newBrowserId,
      browserVersionId: this.state.newBrowserVersionId,
    });
  };

  isBrowserAlreadyExists = (browser) => {
    const result = this.props.user.browsers.find(
      (el) => el.browserId === browser.id && el.browserVersionId === browser.versionId && el.platformId === browser.platformId
    );

    if (result) {
      return true;
    }

    return false;
  };

  onDeleteBrowser = (event, data) => {
    // Same shape as the stored entry: the server removes it by deep equality
    this.props.deleteBrowser({
      platformId: data.platformid,
      browserId: data.browserid,
      browserVersionId: data.browserversionid,
    });
  };

  render() {
    const browserOptions = this.props.browserNames.map((el) => ({ key: el.id, value: el.id, text: el.name }));

    const browserVersionOptions = this.props.browserVersions
      .filter((el) => el.browserId === this.state.newBrowserId)
      .map((el) => ({ key: el.id, value: el.id, text: el.version }));

    const user = this.props.user;
    const platformId = [...user.desktopTypes][0];

    const addBrowserProcessing = this.props.addBrowserStatus.status === OperationStatus.processing;
    const alreadyAdded = this.isBrowserAlreadyExists({ id: this.state.newBrowserId, versionId: this.state.newBrowserVersionId, platformId });
    const addBrowserDisabled = addBrowserProcessing || !this.state.newBrowserId || !this.state.newBrowserVersionId || alreadyAdded;

    const browserNames = this.props.browserNames;
    const browserVersions = this.props.browserVersions;

    const change = (name: string) => (value: any) => this.onChange(null, { name, value: value ?? '' });

    const rows = user.browsers.map((el) => {
      const browser = browserNames.find((b) => b.id === el.browserId);
      const version = browserVersions.find((v) => v.id === el.browserVersionId);
      return {
        ...el,
        platform: getPlatformPrettyName(el.platformId) || el.platformId,
        browser: browser && browser.name ? browser.name : `Unknown browser ${el.browserId}`,
        version: version && version.version ? version.version : `Unknown browser version ${el.browserVersionId}`,
      };
    });

    const title = (label: string) => (
      <Typography component="h3" sx={{ fontSize: 15, fontWeight: 600, color: colors.titleBlue, mb: 1.5 }}>{label}</Typography>
    );

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ maxWidth: 360 }}>
          {title('Your platform')}
          <OptionSelect label="Platform" options={this.platformTypeOptions} value={platformId} onValueChange={change('desktopType')} />
        </Box>

        {!user.desktopTypes.includes(DesktopType.None) && (
          <>
            <Box>
              {title('Add browser')}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' }, gap: 1.5, alignItems: 'start', maxWidth: 760 }}>
                <OptionSelect label="Browser" options={browserOptions} value={this.state.newBrowserId} onValueChange={change('newBrowserId')} />
                <OptionAutocomplete label="Version" options={browserVersionOptions} value={this.state.newBrowserVersionId}
                  onValueChange={change('newBrowserVersionId')} disabled={!this.state.newBrowserId} />
                <Button variant="contained" startIcon={<AddIcon />} disabled={addBrowserDisabled} onClick={this.onAddBrowser} sx={{ height: 56 }}>
                  {addBrowserProcessing ? 'Adding…' : 'Add'}
                </Button>
              </Box>
              {alreadyAdded && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>This browser is already in your list.</Typography>
              )}
            </Box>

            <Box>
              {title('My browsers')}
              <DataTable
                rows={rows}
                rowKey={(row: any) => `${row.platformId}-${row.browserId}-${row.browserVersionId}`}
                emptyText="You haven't added any browsers yet"
                columns={[
                  { key: 'platform', label: 'Platform', render: (row: any) => row.platform },
                  { key: 'browser', label: 'Browser', render: (row: any) => row.browser },
                  { key: 'version', label: 'Version', render: (row: any) => row.version },
                  {
                    key: 'actions',
                    align: 'right',
                    render: (row: any) => (
                      <Button size="small" color="error"
                        onClick={() => this.onDeleteBrowser(null, { platformid: row.platformId, browserid: row.browserId, browserversionid: row.browserVersionId })}>
                        Delete
                      </Button>
                    ),
                  },
                ]}
              />
            </Box>
          </>
        )}
      </Box>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Browsers));
